import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Badge
} from "reactstrap";
import { postDataAPI } from "../../helpers/phpbackend_helper";

const COMMON_ALLERGEN_NAMES = [
  "Milk",
  "Eggs",
  "Fish",
  "Shellfish",
  "Tree Nuts",
  "Peanuts",
  "Wheat",
  "Soy",
  "Sesame",
  "Gluten",
  "Mustard",
  "Celery",
  "Sulphites",
  "Lupin",
  "Molluscs"
];

const capitalizeAllergenName = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const normalizeAllergen = (allergen = {}) => ({
  allergen_id: Number(allergen.allergen_id),
  allergen_name: allergen.allergen_name || ""
});

const isCommonAllergen = (name = "") =>
  COMMON_ALLERGEN_NAMES.some(
    (common) => common.toLowerCase() === name.trim().toLowerCase()
  );

const chipStyle = (isSelected) => ({
  cursor: "pointer",
  border: isSelected ? "1px solid #f1b44c" : "1px solid #ced4da",
  backgroundColor: isSelected ? "#f1b44c" : "#fff",
  color: isSelected ? "#212529" : "#495057",
  fontWeight: isSelected ? 600 : 400,
  padding: "0.4rem 0.75rem",
  borderRadius: "999px",
  userSelect: "none"
});

const AllergenModal = ({ isOpen, toggle, onSave, selectedAllergens = [] }) => {
  const [selected, setSelected] = useState([]);
  const [allergenCatalog, setAllergenCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [togglingName, setTogglingName] = useState("");
  const [showAddOther, setShowAddOther] = useState(false);
  const [otherName, setOtherName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSelected((selectedAllergens || []).map(normalizeAllergen));
    setShowAddOther(false);
    setOtherName("");
    setTogglingName("");
    loadAllergens();
  }, [isOpen, selectedAllergens]);

  const loadAllergens = () => {
    setLoadingCatalog(true);
    postDataAPI({
      serviceName: "item_data",
      op: "get_allergens"
    })
      .then((res) => {
        if (res.status === "ok") {
          setAllergenCatalog((res.data || []).map(normalizeAllergen));
        } else {
          setAllergenCatalog([]);
        }
      })
      .catch(() => setAllergenCatalog([]))
      .finally(() => setLoadingCatalog(false));
  };

  const findCatalogAllergen = (name) =>
    allergenCatalog.find(
      (item) => item.allergen_name.toLowerCase() === name.toLowerCase()
    );

  const ensureAllergen = async (name) => {
    const existing = findCatalogAllergen(name);
    if (existing) return existing;

    const res = await postDataAPI({
      serviceName: "item_data",
      op: "create_allergen",
      allergen_name: name
    });

    if (res.status === "ok" && res.data) {
      const created = normalizeAllergen(res.data);
      setAllergenCatalog((prev) => {
        if (prev.some((a) => a.allergen_id === created.allergen_id)) return prev;
        return [...prev, created];
      });
      return created;
    }

    throw new Error(res.message || "Failed to create allergen");
  };

  const isCommonSelected = (name) =>
    selected.some((a) => a.allergen_name.toLowerCase() === name.toLowerCase());

  const toggleCommonAllergen = async (name) => {
    if (togglingName) return;

    if (isCommonSelected(name)) {
      setSelected((prev) =>
        prev.filter((a) => a.allergen_name.toLowerCase() !== name.toLowerCase())
      );
      return;
    }

    try {
      setTogglingName(name);
      const allergen = await ensureAllergen(name);
      setSelected((prev) => {
        if (prev.some((a) => a.allergen_id === allergen.allergen_id)) return prev;
        return [...prev, allergen];
      });
    } catch (err) {
      alert(err.message || "Failed to select allergen");
    } finally {
      setTogglingName("");
    }
  };

  const removeAllergen = (id) => {
    setSelected((prev) => prev.filter((a) => a.allergen_id !== Number(id)));
  };

  const addOtherAllergen = async () => {
    const name = capitalizeAllergenName(otherName);
    if (!name) {
      alert("Please enter an allergen name");
      return;
    }

    if (selected.some((a) => a.allergen_name.toLowerCase() === name.toLowerCase())) {
      alert("This allergen is already selected");
      return;
    }

    try {
      setCreating(true);
      const allergen = await ensureAllergen(name);
      setSelected((prev) => {
        if (prev.some((a) => a.allergen_id === allergen.allergen_id)) return prev;
        return [...prev, allergen];
      });
      setOtherName("");
      setShowAddOther(false);
    } catch (err) {
      alert(err.message || "Failed to add allergen");
    } finally {
      setCreating(false);
    }
  };

  const otherSelected = selected.filter(
    (allergen) => !isCommonAllergen(allergen.allergen_name)
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>
        ⚠️ Add Allergens
        <div className="text-muted small">Select allergens for this flashcard</div>
      </ModalHeader>

      <ModalBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Badge color="warning">{selected.length} selected</Badge>
        </div>

        <div className="mb-3">
          <div className="fw-medium mb-2">Common allergens</div>
          {loadingCatalog ? (
            <div className="text-muted small">Loading allergens...</div>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {COMMON_ALLERGEN_NAMES.map((name) => {
                const selectedChip = isCommonSelected(name);
                const busy = togglingName === name;
                return (
                  <button
                    key={name}
                    type="button"
                    className="btn btn-sm"
                    style={{
                      ...chipStyle(selectedChip),
                      opacity: busy ? 0.7 : 1
                    }}
                    disabled={!!togglingName}
                    onClick={() => toggleCommonAllergen(name)}
                  >
                    {busy ? "..." : name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-medium">Other allergens</div>
            <Button
              size="sm"
              outline
              onClick={() => setShowAddOther(!showAddOther)}
            >
              + Add other
            </Button>
          </div>

          {otherSelected.length === 0 && !showAddOther && (
            <div className="text-muted small">
              No other allergens selected
            </div>
          )}

          {otherSelected.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mb-2">
              {otherSelected.map((allergen) => (
                <span
                  key={allergen.allergen_id}
                  style={chipStyle(true)}
                  className="d-inline-flex align-items-center gap-2"
                >
                  {allergen.allergen_name}
                  <span
                    style={{ cursor: "pointer", lineHeight: 1 }}
                    onClick={() => removeAllergen(allergen.allergen_id)}
                    role="button"
                    aria-label={`Remove ${allergen.allergen_name}`}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          )}

          {showAddOther && (
            <div className="border rounded p-3 bg-light">
              <Input
                placeholder="Enter allergen name..."
                value={otherName}
                onChange={(e) => setOtherName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!creating) addOtherAllergen();
                  }
                }}
                autoFocus
              />
              <div className="d-flex justify-content-end gap-2 mt-2">
                <Button
                  size="sm"
                  outline
                  onClick={() => {
                    setShowAddOther(false);
                    setOtherName("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  color="warning"
                  disabled={creating || !otherName.trim()}
                  onClick={addOtherAllergen}
                >
                  {creating ? "Adding..." : "Add"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button outline onClick={toggle}>Cancel</Button>
        <Button
          color="primary"
          onClick={() => {
            onSave(selected);
            toggle();
          }}
        >
          Save Allergens
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AllergenModal;
